import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HaveringCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Havering Pothole Claims | 27% U-Roads RED Before Surveys Stopped | Fixtyer"
        description="Havering Council spends well above its DfT allocation and earns a GREEN spend rating, yet overall condition is AMBER — 27% of its 532km U-road network was RED at the last survey in 2022, and the council has not re-surveyed non-strategic routes since."
        canonical="/councils/havering"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Havering', url: '/councils/havering' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Havering spends far more than its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Havering invests heavily from its own budgets — £5.26m capital in 2024/25 against a £333,000 DfT allocation. But the rating that matters for your claim is road condition, and Havering is AMBER overall. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 532km — roughly three-quarters of Havering\'s 713km carriageway network. At the January–April 2022 Detailed Visual Inspection, 27.16% of U-roads were in RED condition — approximately 144km of estate streets and residential routes. The council has not gathered further condition data on non-strategic routes since that survey.',
          },
          {
            question: 'The council stopped re-surveying local roads in 2022 — does that help my claim?',
            answer: 'It can. Havering\'s own report states it "have taken the decision not to gather any further road condition data on non-strategic routes since 2022." For a Section 58 defence, the council must show a reasonable system for knowing road condition. A network where 75% of carriageways have not been condition-surveyed in over three years is a harder case to defend than one with current data.',
          },
          {
            question: 'Pothole repairs fell to 2,006 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Havering filled 2,006 potholes in 2024/25, down from 3,080 the previous year — but footway resurfacing collapsed to just 0.86 miles (from 14.73 miles in 2020/21), and the council cut highway spending that year as part of its Medium Term Financial Strategy. Fewer pothole fills alongside reduced planned maintenance usually means fewer reactive jobs logged, not fewer defects forming.',
          },
          {
            question: 'Does Havering\'s Highways Maintenance Plan make claims harder?',
            answer: 'The council adopted a new Highways Maintenance Plan Policy in December 2024, aligned to the Well Managed Highway Infrastructure code. The report explicitly states this provides "a robust approach to the repudiation of insurance and public liability claims." Expect a structured Section 58 defence — but that defence still depends on proving reasonable inspection of your specific road, which is harder where condition data has not been updated since 2022.',
          },
          {
            question: 'What does the £6 million steady-state figure mean for my claim?',
            answer: 'Havering\'s 2022 survey analysis found £6m per annum — £30m over five years — was required merely to prevent further overall network deterioration. The council says it invests that steady-state value each year, yet also cut spending in 2024/25 and reports 26–27% RED rates on B/C and U-roads from the last snapshot. If the network needed £6m just to stand still, roads in RED condition are, by the council\'s own analysis, in the backlog.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Havering Council"
        councilSlug="havering"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/havering"
        headline="Havering Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Havering</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Havering Stopped Counting Local Roads in 2022 — 27% Were Already RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Havering earns a <strong className="text-emerald-400">GREEN</strong> DfT spend rating by investing 
            millions beyond its capital allocation. Yet the overall scorecard is <strong className="text-amber-400">AMBER</strong> — 
            because at the last Detailed Visual Inspection in early 2022, <strong className="text-rose-400">27.16% of 
            the borough's 532km U-road network was in RED condition</strong>, and the council then decided not to 
            gather any further condition data on non-strategic routes.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">10,145</div>
            <div className="text-white font-medium mt-2">Potholes filled in four years</div>
            <div className="text-brand-300 text-sm mt-1">
              2,794 in 2021/22, 2,265 in 2022/23, 3,080 in 2023/24 and 2,006 in 2024/25 — 
              on a 713km carriageway network where planned resurfacing has fallen sharply.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            A single UKPMS Detailed Visual Inspection snapshot from January–April 2022 — 
            the last time Havering published network-wide condition by road class
          </p>

          {/* Network overview */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Havering's 713km carriageway network</h3>
            <div className="grid sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">60km</div>
                <div className="text-sm text-surface-600">A-roads</div>
                <div className="text-xs text-surface-500">8.4% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">121km</div>
                <div className="text-sm text-surface-600">B and C roads</div>
                <div className="text-xs text-surface-500">17.0% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">532km</div>
                <div className="text-sm text-rose-800">U-roads</div>
                <div className="text-xs text-surface-500">74.6% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">1,040km</div>
                <div className="text-sm text-surface-600">Footways</div>
                <div className="text-xs text-surface-500">plus 113km public rights of way</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Three-quarters of Havering's carriageways are unclassified residential and estate roads. 
              The condition picture below is from one survey over three years ago — not a rolling five-year trend.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (60km — 8.4% of network): January–April 2022 snapshot</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">23.16%</div>
                <div className="text-sm text-rose-800">RED</div>
                <div className="text-xs text-surface-500">~14km needing maintenance</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">15.27%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">61.21%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Even on principal routes, nearly a quarter of A-road length was RED at the last survey. 
              Havering does participate in pan-London strategic network data gathering — but this DVI 
              snapshot is what the council published for borough-wide condition.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (121km — 17% of network): January–April 2022 snapshot</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">26.27%</div>
                <div className="text-sm text-rose-800">RED</div>
                <div className="text-xs text-surface-500">~32km needing maintenance</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">20.88%</div>
                <div className="text-sm text-amber-800">Amber</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">52.71%</div>
                <div className="text-sm text-emerald-800">Green</div>
              </div>
            </div>
            <p className="text-amber-800 font-medium">
              More than a quarter of B and C road length was RED — and a further 21% amber. 
              Under half the classified local road network was in good condition at the last measurement.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (532km — 74.6% of network): RED only published</h3>
            <div className="text-center mb-4">
              <div className="text-5xl font-bold text-rose-600">27.16%</div>
              <div className="text-sm text-rose-800 font-medium">RED condition at last survey</div>
              <div className="text-xs text-surface-500 mt-1">approximately 144km of residential and estate roads</div>
            </div>
            <p className="text-rose-800 text-sm">
              Havering's transparency report publishes only the RED percentage for unclassified roads — 
              not a full amber/green breakdown. What is clear: more than one in four U-road kilometres 
              already needed maintenance when the council stopped surveying this network entirely.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The £6 Million Steady-State Gap</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6m</div>
                <div className="text-brand-300 text-sm">Annual investment to prevent further deterioration</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£30m</div>
                <div className="text-brand-300 text-sm">Five-year steady-state requirement from 2022 analysis</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">2022</div>
                <div className="text-brand-300 text-sm">Last borough-wide condition survey date</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Havering's own survey analysis found <span className="text-emerald-400">£6m per annum</span> was 
              required merely to hold the network at steady state — yet 26–27% of B/C and U-roads were 
              already RED at that snapshot, and the council has not re-measured non-strategic routes since.
            </p>
          </div>
        </div>
      </section>

      {/* The 532km Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 532km Monitoring Gap
          </h2>
          <p className="text-surface-600 mb-8">
            Three-quarters of Havering's carriageways — with no condition update since early 2022
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Due to the significant costs and time involved with gathering the data for the Detailed Visual Assessment the Council have taken the decision not to gather any further road condition data on non-strategic routes since 2022."
              source="Havering Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What "Non-Strategic" Means Here</h3>
              <p className="text-surface-600 text-sm mb-3">
                Havering's U-road network alone is 532km — the estate streets, residential crescents and 
                local connectors where most pothole damage claims originate. The council's 2022 UKPMS 
                Detailed Visual Inspection covered all roads between January and April that year.
              </p>
              <p className="text-surface-600 text-sm">
                Since then, no further condition data has been gathered on these routes. The council 
                participates in pan-London strategic network monitoring, and is trialling Vaisala Road AI 
                software — but published borough-wide U-road condition remains frozen at the 2022 snapshot.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 144km Question</h3>
              <p className="text-amber-800 text-sm mb-3">
                At 27.16% RED on 532km of U-roads, roughly <strong>144km of residential carriageway</strong> was 
                already classified as needing maintenance when monitoring stopped. Without re-surveying, 
                the council cannot demonstrate those roads improved — only that it continued filling potholes reactively.
              </p>
              <p className="text-amber-800 text-sm">
                Analysis of the DVI "provided Officers with a snapshot of the condition of the borough's 
                roads during this period" — a snapshot, not a live dashboard.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, a council must show it had a reasonable system for 
              knowing the condition of its roads. For Havering's U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and has it been surveyed at all since 2022?</li>
              <li>• If 27% of U-roads were RED at the last measurement, what was done about yours specifically?</li>
              <li>• How does the council track deterioration on roads it no longer condition-surveys?</li>
              <li>• Does reactive pothole filling alone satisfy the duty under Section 41 to maintain roads to a reasonable standard?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network it stopped measuring — 
              while 144km of residential roads were already in RED condition at the last count.
            </p>
          </div>
        </div>
      </section>

      {/* Spending and the MTFS Cut */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend, AMBER Condition
          </h2>
          <p className="text-surface-600 mb-8">
            Havering invests heavily from its own budgets — but cut highway spending in 2024/25
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£8,890,799</td>
                  <td className="px-4 py-3 text-center">£2,962,893</td>
                  <td className="px-4 py-3 text-center">75.0%</td>
                  <td className="px-4 py-3 text-center">25.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£12,932,716</td>
                  <td className="px-4 py-3 text-center">£3,157,372</td>
                  <td className="px-4 py-3 text-center">80.4%</td>
                  <td className="px-4 py-3 text-center">19.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£7,949,133</td>
                  <td className="px-4 py-3 text-center">£3,514,037</td>
                  <td className="px-4 py-3 text-center">69.3%</td>
                  <td className="px-4 py-3 text-center">30.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£333,000</td>
                  <td className="px-4 py-3 text-center">£6,470,912</td>
                  <td className="px-4 py-3 text-center">£3,875,096</td>
                  <td className="px-4 py-3 text-center">62.5%</td>
                  <td className="px-4 py-3 text-center">37.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£333,000</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">£5,257,147</td>
                  <td className="px-4 py-3 text-center">£3,150,701</td>
                  <td className="px-4 py-3 text-center">62.5%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">37.5%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£1,082,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£7,689,860</td>
                  <td className="px-4 py-3 text-center">£3,031,000</td>
                  <td className="px-4 py-3 text-center">71.7%</td>
                  <td className="px-4 py-3 text-center">28.3%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="However it should be noted that in the 2024/25 financial year the Council made the decision to reduce spending as part of its Medium Term Financial Strategy for that year only. Spending levels were then reapplied to similar previous levels thereafter."
              source="Havering Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Why Spend Is GREEN</h3>
              <p className="text-emerald-800 text-sm mb-3">
                In 2024/25 Havering spent £5.26m capital against a £333,000 DfT allocation — roughly 
                fifteen times the central grant. Total highway spend (capital plus revenue) exceeded £8.4m. 
                The DfT Spend scorecard rewards authorities that invest beyond the formula grant.
              </p>
              <p className="text-emerald-800 text-sm">
                The council states it invests the £6m steady-state resurfacing value each year through its 
                Highways Improvement Programme — currently in its third year, with yearly programmes published online.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why Condition Is Still AMBER</h3>
              <p className="text-amber-800 text-sm mb-3">
                Reactive maintenance share rose to 37.5% in 2023/24 and 2024/25 — up from 19.6% in 2021/22. 
                Capital spend fell from £12.9m to £5.3m over the same period. The 2024/25 MTFS cut came 
                the same year footway resurfacing dropped to 0.86 miles.
              </p>
              <p className="text-amber-800 text-sm">
                Best Practice is AMBER partly because the council stopped borough-wide condition surveys 
                and relies on a 2022 snapshot plus strategic-route pan-London data and an unproven Road AI trial.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Potholes and Resurfacing */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            10,145 Potholes — And Falling Resurfacing
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repairs continue, but planned maintenance outputs have collapsed on footways
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageways resurfaced (miles)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Footways resurfaced (miles)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No data</td>
                  <td className="px-6 py-4 text-center">21.5</td>
                  <td className="px-6 py-4 text-center">14.73</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,794</td>
                  <td className="px-6 py-4 text-center">20.96</td>
                  <td className="px-6 py-4 text-center">17.32</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,265</td>
                  <td className="px-6 py-4 text-center">8.43</td>
                  <td className="px-6 py-4 text-center">8.97</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,080</td>
                  <td className="px-6 py-4 text-center">10.18</td>
                  <td className="px-6 py-4 text-center">3.13</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,006</td>
                  <td className="px-6 py-4 text-center">10.3</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">0.86</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year pothole total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,145</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Planned vs Reactive</h3>
              <p className="text-surface-600 text-sm mb-3">
                Havering's report draws a direct link: planned maintenance means lengths of road resurfaced; 
                reactive maintenance means potholes filled. The council notes that higher capital investment 
                "would generally result in less burden on the Council's reactive maintenance budget."
              </p>
              <p className="text-surface-600 text-sm">
                Footway resurfacing fell from 14.73 miles in 2020/21 to 0.86 miles in 2024/25 — a 94% drop. 
                Potholes on pavements and carriageways are symptoms of the same underlying asset decline.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Backlog Admission</h3>
              <QuoteCard
                quote="In simple terms the higher the level of investment in the highway the greater the improvement will be to the overall condition of the network and the lower the level of investment the greater the backlog (ie those roads needing maintenance) would be at the conclusion of the investment programme."
                source="Havering Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Highways Improvement Programme — Year Three</h3>
            <p className="text-brand-200 mb-4">
              Havering ranks roads from its 2022 DVI data, then applies weightings — speed limits, bus routes, 
              schools, reactive spend history, ward councillor requests — to produce the Highways Improvement Programme. 
              Officers conduct site assessments on top-ranked roads each year before agreeing works with councillors.
            </p>
            <p className="text-brand-300 text-sm mb-4">
              The programme is asset-based and published yearly. But the underlying condition ranking still 
              rests on a 2022 survey the council chose not to repeat — while trialling Vaisala Road AI for future use.
            </p>
            <p className="text-white font-medium">
              A pothole on a road not yet reached by the HIP queue is, by the council's own backlog logic, 
              part of the maintenance debt — not an isolated defect.
            </p>
          </div>
        </div>
      </section>

      {/* The Claims Repudiation Admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Highways Maintenance Plan — Built For Repudiation
          </h2>
          <p className="text-surface-600 mb-8">
            Havering adopted a new inspection and claims policy in December 2024 — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="This also ensures that the authority discharges its statutory duty to maintain the highway whilst also providing a robust approach to the repudiation of insurance and public liability claims on the Council's highway network."
              source="Havering Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What The HMPP Does</h3>
              <p className="text-surface-600 text-sm mb-3">
                The Highways Maintenance Plan Policy follows the UK Roads Liaison Group's Well Managed Highway 
                Infrastructure code. It sets risk-based inspection frequencies, defect prioritisation and repair 
                standards for reactive maintenance — and explicitly targets insurance claim rejection.
              </p>
              <p className="text-surface-600 text-sm">
                Expect a documented Section 58 defence citing inspection records and repair thresholds. 
                Generic "the council doesn't maintain roads" arguments will not work here.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Where The Policy Meets Its Limits</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Condition data on 532km of U-roads has not been updated since 2022</li>
                <li>• 27% of those U-roads were RED at the last count — the backlog the policy does not erase</li>
                <li>• Reactive share hit 37.5% in 2024/25 — defects forming faster than planned works prevent</li>
                <li>• Prior reports and photos still prove actual notice of your specific defect</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Havering invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend far exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published Highways Improvement Programme with asset-based prioritisation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> UKPMS-aligned inspection and new HMPP aligned to national best practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £6m steady-state investment target — council says it meets this annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented claims repudiation framework — expect organised Section 58 responses
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured defence on principal routes with current pan-London monitoring. Vague claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 26–27% RED on B/C and U-roads at last survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No condition re-survey on non-strategic routes since 2022
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 532km U-road network — ~144km was RED when monitoring stopped
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10,145 potholes filled in four years — reactive burden proves defects keep forming
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2024/25 MTFS spending cut and footway resurfacing down to 0.86 miles
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive maintenance share rose to 37.5% — planned prevention losing ground
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a claims-focused maintenance policy, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 2022 survey gap and 27% RED rate are your strongest structural arguments</li>
              <li>• Whether your street appears on the published HIP programme — or is still in the unmeasured backlog</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Havering's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Havering?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 2022 survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No HIP backlog analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 27% U-road RED rate documented</li>
                <li>✅ 2022 monitoring gap argued</li>
                <li>✅ 10,145 potholes in four years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Havering</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=havering"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Havering Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Havering spends far more than its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Havering invests heavily from its own budgets — 
                £5.26m capital in 2024/25 against a £333,000 DfT allocation. But the rating that matters for 
                your claim is road condition, and Havering is AMBER overall. Section 58 turns on whether the 
                specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 532km — roughly three-quarters of Havering's 713km carriageway network. At the 
                January–April 2022 Detailed Visual Inspection, 27.16% of U-roads were in RED condition — 
                approximately 144km of estate streets and residential routes. The council has not gathered further 
                condition data on non-strategic routes since that survey.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council stopped re-surveying local roads in 2022 — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Havering's own report states it "have taken the decision not to gather any further road 
                condition data on non-strategic routes since 2022." For a Section 58 defence, the council must show 
                a reasonable system for knowing road condition. A network where 75% of carriageways have not been 
                condition-surveyed in over three years is a harder case to defend than one with current data.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 2,006 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Havering filled 2,006 potholes in 2024/25, down from 3,080 the previous year — but footway 
                resurfacing collapsed to just 0.86 miles (from 14.73 miles in 2020/21), and the council cut highway 
                spending that year as part of its Medium Term Financial Strategy. Fewer pothole fills alongside reduced 
                planned maintenance usually means fewer reactive jobs logged, not fewer defects forming.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Havering's Highways Maintenance Plan make claims harder?
              </h3>
              <p className="text-surface-600 text-sm">
                The council adopted a new Highways Maintenance Plan Policy in December 2024, aligned to the Well 
                Managed Highway Infrastructure code. The report explicitly states this provides "a robust approach to 
                the repudiation of insurance and public liability claims." Expect a structured Section 58 defence — 
                but that defence still depends on proving reasonable inspection of your specific road, which is harder 
                where condition data has not been updated since 2022.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the £6 million steady-state figure mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Havering's 2022 survey analysis found £6m per annum — £30m over five years — was required merely to 
                prevent further overall network deterioration. The council says it invests that steady-state value each 
                year, yet also cut spending in 2024/25 and reports 26–27% RED rates on B/C and U-roads from the last 
                snapshot. If the network needed £6m just to stand still, roads in RED condition are, by the council's 
                own analysis, in the backlog.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.havering.gov.uk/downloads/download/1080/highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Havering Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
